import { SxProps, SystemStyleObject, Theme } from '@mui/system';
import { useMemo } from 'react';

type SxPropsList = ReadonlyArray<boolean | SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>)>;

/**
 * Shallowly merges two `sx` props arrays or objects.
 * Useful for components that want to set default `sx` props on the root JSX element they render,
 * while also allowing consumers of the component to override or extend those styles via an `sx`
 * prop on the component.
 *
 * @param baseSxProps The `sx` value that the component wants to set on it's root JSX element.
 * @param overrideSxProps The optional `sx` value the component may have received as it's prop,
 *                        that should be used to override or extend the `baseSxProps`.
 * @returns The shallow merged `sx` value.
 */
export function useShallowMergeSxProps(baseSxProps: SxProps<Theme>, overrideSxProps?: SxProps<Theme>) {
    const shallowMergedSXProps = useMemo(() => {
        if (overrideSxProps === undefined) {
            return baseSxProps;
        }

        const baseSXPropsResolved: SxPropsList = Array.isArray(baseSxProps) ? baseSxProps : [baseSxProps];

        const overrideSXPropsResolved: SxPropsList = Array.isArray(overrideSxProps)
            ? overrideSxProps
            : [overrideSxProps];

        const mergedSXProps = [...baseSXPropsResolved, ...overrideSXPropsResolved];

        return mergedSXProps;
    }, [baseSxProps, overrideSxProps]);

    return shallowMergedSXProps;
}
